<template>
  <v-list-item
    class="order-cell d-flex align-center"
    :class="{
      'order-cell--no-brand-access': !hasBrandAccessToOrder,
      'order-cell--mobile-typography': $vuetify.breakpoint.smAndDown,
    }"
    :link="hasBrandAccessToOrder"
    @keydown="$emit('keydown-emit', $event)"
    @keydown.enter.prevent="emitActivate"
    @keydown.space.prevent="emitActivate"
    @focus="$emit('focus', $event)"
    @click="emitActivate"
  >
    <v-list-item-avatar
      tile
      class="rounded-lg flex-shrink-0"
      :class="{ background2: !rawStatusValue }"
      :style="avatarStyle"
      :size="$vuetify.breakpoint.smAndDown ? 44 : 50"
    >
      <v-icon
        v-if="rawStatusValue"
        :size="$vuetify.breakpoint.smAndDown ? 22 : 26"
        :color="statusColor"
        v-text="statusIconSymbol"
      ></v-icon>
      <v-icon
        v-else
        :size="$vuetify.breakpoint.smAndDown ? 22 : 26"
        color="grey darken-1"
      >
        mdi-receipt-text-outline
      </v-icon>
    </v-list-item-avatar>

    <v-list-item-content
      class="min-width-0 order-cell__content"
      :class="$vuetify.breakpoint.smAndDown ? 'py-1' : 'py-2'"
    >
      <v-list-item-title>
        <span
          :class="[
            'text-wrap font-weight-bold',
            $vuetify.breakpoint.smAndDown ? 'text-body-2' : '',
          ]"
        >
          {{ internalInvoiceLabel
          }}<span
            v-if="externalInvoiceParen"
            class="font-weight-medium text--secondary"
          >
            {{ externalInvoiceParen }}</span
          >
        </span>
      </v-list-item-title>
      <v-list-item-subtitle>
        <span
          :class="[
            'text-capitalize text-wrap font-weight-medium',
            $vuetify.breakpoint.smAndDown
              ? 'text-caption text--secondary'
              : 'text-body-2 text--secondary',
          ]"
        >
          {{ accountSubtitleLine }}
        </span>
      </v-list-item-subtitle>
      <v-list-item-subtitle class="order-cell__amounts-subtitle">
        <div
          class="order-cell__amounts"
          :class="
            $vuetify.breakpoint.smAndDown
              ? 'order-cell__amounts--grid'
              : 'order-cell__amounts--inline'
          "
        >
          <div class="order-cell__metric text-caption">
            <span class="order-cell__amount-label text--secondary font-weight-medium"
              >Total:</span
            >
            <span
              class="order-cell__amount-value font-weight-bold"
              :style="{ color: $vuetify.theme.currentTheme.primary }"
              >{{ formatOrderCurrency(totalAmount) }}</span
            >
          </div>
          <div class="order-cell__metric text-caption">
            <span class="order-cell__amount-label text--secondary font-weight-medium"
              >Paid:</span
            >
            <span
              class="order-cell__amount-value font-weight-bold"
              :style="{ color: $vuetify.theme.currentTheme.success }"
              >{{ formatOrderCurrency(paidAmount) }}</span
            >
          </div>
          <div class="order-cell__metric text-caption">
            <span class="order-cell__amount-label text--secondary font-weight-medium"
              >Balance:</span
            >
            <span
              class="order-cell__amount-value font-weight-bold"
              :style="{ color: $vuetify.theme.currentTheme.error }"
              >{{ formatOrderCurrency(balanceAmount) }}</span
            >
          </div>
        </div>
      </v-list-item-subtitle>
    </v-list-item-content>

    <v-list-item-action>
      <div class="d-flex align-center" style="gap: 8px">
        <StatusChip
          v-if="showStatus && statusChipPayload && $vuetify.breakpoint.mdAndUp"
          no-top-margin
          :status="statusChipPayload"
          class="text-capitalize"
        />
        <v-btn
          v-if="hasBrandAccessToOrder"
          icon
          small
          aria-label="Open order in new tab"
          @click.stop.prevent="openOrderInNewTab"
        >
          <v-icon small color="review">mdi-open-in-new</v-icon>
        </v-btn>
      </div>
    </v-list-item-action>
  </v-list-item>
</template>

<script>
import { mapGetters } from "vuex";
import StatusChip from "@/components/misc/ui/chips/StatusChip.vue";

export default {
  name: "OrderCell",
  components: {
    StatusChip,
  },
  props: {
    order: {
      type: Object,
      required: true,
    },
    showStatus: {
      type: Boolean,
      default: true,
    },
  },
  computed: {
    ...mapGetters(["getOrderStatus", "getUser"]),
    /** Same id resolution as AppBar `getOrderHitId` / `openSalesOrderFromSearch`. */
    salesOrderRouteId() {
      const o = this.order || {};
      return String(
        o.objectID || o.id || o.salesOrderId || o.orderId || "",
      ).trim();
    },
    nestedBalance() {
      const o = this.order || {};
      return o.balance && typeof o.balance === "object" ? o.balance : null;
    },
    internalInvoiceLabel() {
      const o = this.order || {};
      const n =
        o["orderNumber.internal"] ??
        o.orderNumberInternal ??
        o.order_internal ??
        o?.orderNumber?.internal;
      const s = n != null && String(n).trim() !== "" ? String(n).trim() : "";
      const label = s || this.fallbackId || "Invoice";
      return String(label).toUpperCase();
    },
    externalInvoiceRaw() {
      const o = this.order || {};
      const n =
        o["orderNumber.external"] ??
        o.orderNumberExternal ??
        o.order_external ??
        o?.orderNumber?.external;
      return n != null && String(n).trim() !== "" ? String(n).trim() : "";
    },
    externalInvoiceParen() {
      const ext = this.externalInvoiceRaw;
      return ext ? ` (${ext})` : "";
    },
    accountNameLabel() {
      const o = this.order || {};
      const n =
        o["account.name"] ??
        o.accountName ??
        o.account_name ??
        o?.account?.name ??
        "";
      return String(n).trim();
    },
    brandLabel() {
      const o = this.order || {};
      const n =
        o["brand.name"] ??
        o.brandName ??
        o.brand_name ??
        o?.brand?.name ??
        (typeof o.brand === "string" ? o.brand : "") ??
        "";
      return String(n).trim();
    },
    orderBrandId() {
      const o = this.order || {};
      const raw =
        o["brand.id"] ??
        o.brandId ??
        o.brand_id ??
        o?.brand?.id;
      if (raw == null || String(raw).trim() === "") return "";
      return String(raw).trim();
    },
    hasBrandAccessToOrder() {
      const user = this.getUser;
      if (!user || user.accountType >= 4) return true;
      const bid = this.orderBrandId;
      if (!bid) return true;
      const allowed = Array.isArray(user.brands) ? user.brands.map(String) : [];
      return allowed.includes(bid);
    },
    accountSubtitleLine() {
      const account = this.accountNameLabel;
      const brand = this.brandLabel;
      if (account && brand) return `${account} · ${brand}`;
      if (account) return account;
      if (brand) return brand;
      return "—";
    },
    fallbackId() {
      const o = this.order || {};
      return String(
        o.objectID || o.id || o.salesOrderId || o.orderId || "",
      ).trim();
    },
    totalAmount() {
      const o = this.order || {};
      const nested = this.nestedBalance;
      const value =
        o.balanceTotal ??
        o.balance_total ??
        nested?.total ??
        o.total ??
        o.balance?.total;
      return Number(value) || 0;
    },
    dueAmount() {
      const o = this.order || {};
      const nested = this.nestedBalance;
      const value =
        o.balanceDue ?? o.balance_due ?? nested?.due ?? o.due ?? o.balance?.due;
      return Number(value) || 0;
    },
    paidAmount() {
      const paid = this.totalAmount - this.dueAmount;
      return paid >= 0 ? paid : 0;
    },
    balanceAmount() {
      const o = this.order || {};
      const nested = this.nestedBalance;
      const explicit =
        o.balanceRemaining ??
        o.balance_remaining ??
        o.outstandingBalance ??
        o.outstanding_balance ??
        nested?.balance ??
        nested?.remaining;
      if (explicit != null && String(explicit).trim() !== "") {
        const n = Number(explicit);
        if (!Number.isNaN(n)) return n;
      }
      return this.dueAmount;
    },
    rawStatusValue() {
      return String(this.order?.status ?? "").trim();
    },
    resolvedStatus() {
      const list = this.getOrderStatus || [];
      const raw = this.rawStatusValue;
      if (!raw) return null;
      let found = list.find((o) => o.value === raw);
      if (!found) {
        const lower = raw.toLowerCase();
        found = list.find((o) => o.value.toLowerCase() === lower);
      }
      return found || null;
    },
    statusDisplayText() {
      const raw = this.rawStatusValue;
      if (!raw) return "";
      if (this.resolvedStatus) return this.resolvedStatus.text;
      return raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
    },
    statusColor() {
      return this.resolvedStatus?.color ?? "#9e9e9e";
    },
    /** Same resolution as SalesOrder_New.vue (previous orders avatar). */
    statusColorResolved() {
      const c = this.statusColor;
      if (["error", "products"].includes(c)) {
        return this.$vuetify.theme.currentTheme[c];
      }
      return c;
    },
    avatarStyle() {
      if (!this.rawStatusValue) {
        return {
          boxShadow:
            "rgba(0, 0, 0, 0.02) 0px 1px 3px 0px, rgba(27, 31, 35, 0.15) 0px 0px 0px 1px",
          border: `1px solid ${this.$vuetify.theme.currentTheme.background3} !important`,
        };
      }
      return {
        background: `${this.statusColorResolved}33`,
      };
    },
    statusChipPayload() {
      if (!this.rawStatusValue) return null;
      return {
        value: this.resolvedStatus?.value ?? this.rawStatusValue,
        text: this.statusDisplayText,
        color: this.statusColor,
      };
    },
    statusIconSymbol() {
      const value = this.resolvedStatus?.value ?? this.rawStatusValue;
      return this.getStatusIconForValue(value);
    },
    dateText() {
      return this.getOrderHitDateText(this.order);
    },
  },
  methods: {
    /**
     * Matches SalesOrder_New.vue (previous orders list): icon set for each status.value.
     */
    getStatusIconForValue(value) {
      const v = String(value || "").trim();
      if (!v) return "$delete";
      switch (v) {
        case "complete":
          return "$checkCircle";
        case "shipped":
          return "$delivery";
        case "fulfilled":
          return "$deliveryReady";
        case "deliveryConfirmation":
          return "$deliveryConfirmation";
        case "accepted":
          return "$orderComplete";
        case "submitted":
          return "$order";
        case "draft":
          return "$awaitingApproval";
        default:
          break;
      }
      const lower = v.toLowerCase();
      const byLower = {
        complete: "$checkCircle",
        shipped: "$delivery",
        fulfilled: "$deliveryReady",
        deliveryconfirmation: "$deliveryConfirmation",
        accepted: "$orderComplete",
        submitted: "$order",
        draft: "$awaitingApproval",
        cancelled: "$delete",
        rejected: "$delete",
      };
      return byLower[lower] ?? "$delete";
    },
    emitActivate() {
      if (!this.hasBrandAccessToOrder) {
        this.notifyBrandAccessDenied();
        return;
      }
      this.$emit("activate", this.order);
    },
    openOrderInNewTab() {
      if (!this.hasBrandAccessToOrder) {
        this.notifyBrandAccessDenied();
        return;
      }
      const id = this.salesOrderRouteId;
      if (!id) {
        console.error("OrderCell: missing route id for order", this.order);
        return;
      }
      const link = this.$router.resolve({
        name: "salesOrder-view",
        params: { id },
      });
      window.open(link.href, "_blank", "noopener,noreferrer");
    },
    notifyBrandAccessDenied() {
      const brandName = this.brandLabel || "this brand";
      if (this.$root.toast?.show) {
        this.$root.toast.show({
          title: "No access",
          subtitle: `You don't have access to orders for ${brandName}.`,
          color: this.$vuetify.theme.currentTheme.error,
          icon: "mdi-lock-alert",
        });
      } else {
        console.error(
          "OrderCell: user cannot access brand",
          this.orderBrandId,
        );
      }
    },
    formatOrderCurrency(value) {
      try {
        return new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: "USD",
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(Number(value) || 0);
      } catch (error) {
        console.error("Error formatting order currency:", error);
        return "$0.00";
      }
    },
    getOrderHitDateText(orderHit) {
      const rawDate =
        orderHit?.shipDate ||
        orderHit?.ship_date ||
        orderHit?.created ||
        orderHit?.created_at ||
        orderHit?.lastmodified ||
        orderHit?.lastModified;
      if (rawDate == null) return "";
      const parsed =
        typeof rawDate === "number" ? new Date(rawDate) : new Date(rawDate);
      if (Number.isNaN(parsed.getTime())) return "";
      return `Date: ${parsed.toLocaleDateString()}`;
    },
  },
};
</script>

<style scoped>
.order-cell--no-brand-access {
  opacity: 0.65;
  cursor: not-allowed;
}

.order-cell__content {
  min-width: 0;
}

/* Tablet/desktop: one flowing line, middot separators */
.order-cell__amounts--inline {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  row-gap: 4px;
}

.order-cell__amounts--inline .order-cell__metric {
  display: inline-flex;
  align-items: baseline;
  flex-wrap: nowrap;
  white-space: nowrap;
  max-width: 100%;
}

.order-cell__amounts--inline .order-cell__amount-label {
  margin-right: 4px;
}

.order-cell__amounts--inline .order-cell__metric:not(:last-child)::after {
  content: "·";
  margin-left: 8px;
  margin-right: 8px;
  color: rgba(0, 0, 0, 0.26);
}

.theme--dark .order-cell__amounts--inline .order-cell__metric:not(:last-child)::after {
  color: rgba(255, 255, 255, 0.3);
}

/* Phone/small tablet: 3 equal columns — label over value, no orphan Balance line */
.order-cell__amounts--grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  column-gap: 6px;
  width: 100%;
}

.order-cell__amounts--grid .order-cell__metric {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 1px;
  min-width: 0;
}

.order-cell__amounts--grid .order-cell__amount-label {
  font-size: 0.65rem;
  letter-spacing: 0.02em;
  line-height: 1.2;
  white-space: nowrap;
}

.order-cell__amounts--grid .order-cell__amount-value {
  font-size: 0.75rem;
  line-height: 1.25;
  white-space: nowrap;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
}

.order-cell__amount-value {
  white-space: nowrap;
}

/* Match AccountCell.vue: list density + line-height on smAndDown */
.order-cell--mobile-typography >>> .v-list-item__title {
  line-height: 1.35 !important;
}

.order-cell--mobile-typography >>> .v-list-item__subtitle {
  line-height: 1.4 !important;
  margin-top: 2px !important;
}

.order-cell--mobile-typography
  >>> .v-list-item__subtitle
  + .v-list-item__subtitle {
  margin-top: 2px !important;
}

/* smAndDown: no status chip in body — give the money row clear separation as the bottom block */
.order-cell--mobile-typography
  >>> .v-list-item__subtitle.order-cell__amounts-subtitle {
  margin-top: 10px !important;
}
</style>
